import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ModalAtom from "../../../../../components/atoms/modal/modal";
import {
	applyAufmassResultRaw,
	markDirtyAfterMutation,
	selectAddedGroupsV2,
	selectCarriedForwardDataV2,
	selectFormDataV2,
	selectPositionsV2,
	setAddGroupModal,
	setAufmassError,
} from "../../../../../store/slices/aufmassV2Slice";
import { selectCurrentProjectId } from "../../../../../store/slices/projectDetailsSlice";
import {
	useAddAufmassGroupMutation,
	useUpdatePositionCommentsMutation,
	useValidateInvoicePositionMutation,
} from "../../../../../store/services/aufmass";
import { showToast } from "../../../../../utils/method";
import AufmassPositionTableRow from "./AufmassPositionTableRow";
import CarriedForwardSectionV2 from "./CarriedForwardSectionV2";
import NewMeasurementSectionV2 from "./NewMeasurementSectionV2";
import "./ExpandablePositionsTable.scss";

// ExpandablePositionsTable component for rendering position details in aufmass context
const ExpandablePositionsTable = React.memo(
	({
		positions,
		expandedMeasurements,
		toggleMeasurement,
		onMeasurementChange,
		isReadonly = false,
		positionsData = {},
	}) => {
		const { t } = useTranslation();
		const dispatch = useDispatch();
		const [showValidateModal, setShowValidateModal] = useState(false);
		const [validatePositionId, setValidatePositionId] = useState(null);
		const [isValidating, setIsValidating] = useState(false);

		// Redux selectors for measurement data
		const addedGroups = useSelector(selectAddedGroupsV2);
		const carriedForwardData = useSelector(selectCarriedForwardDataV2);
		const reduxPositions = useSelector(selectPositionsV2);
		const formData = useSelector(selectFormDataV2);
		const invoiceData = useSelector((state) => state.aufmassV2.invoiceData) || {};
		const projectId = useSelector(selectCurrentProjectId);
		const [addAufmassGroup] = useAddAufmassGroupMutation();
		const [validateInvoicePosition] = useValidateInvoicePositionMutation();
		const [updatePositionComments] = useUpdatePositionCommentsMutation();

		// State for saving status
		const [savingQuickEntry, setSavingQuickEntry] = useState({});

		// Handler for quick entry field blur - creates initial group only (locked after first entry)
		const handleQuickEntryBlur = useCallback(async (position, value) => {
			if (!position || !value || value.trim() === '') return;

			// Validate quantity
			const numericQuantity = parseFloat(value.replace(',', '.'));
			if (isNaN(numericQuantity) || numericQuantity <= 0) {
				showToast(t("aufmass.invalid-quantity"), { type: "error" });
				return;
			}

			setSavingQuickEntry(prev => ({ ...prev, [position.id]: true }));

			try {
				const reduxPositionData = reduxPositions[position.id];
				const invoicePositionId = reduxPositionData?.invoicePositionId;

				if (!invoicePositionId) {
					throw new Error("Invoice position ID not found");
				}

				// Check if there are any existing groups - if so, quick entry should be disabled
				const existingGroups = addedGroups[invoicePositionId] || [];
				if (existingGroups.length > 0) {
					showToast(t("aufmass.quick-entry-disabled-after-creation"), { type: "warning" });
					return;
				}

				// Get previous aufmass quantity (from carried forward data)
				const carriedForwardGroups = carriedForwardData[invoicePositionId] || [];
				const previousQuantity = carriedForwardGroups.reduce((sum, group) => {
					return sum + (group.lines || []).reduce((lineSum, line) =>
						lineSum + parseFloat(line.quantity || 0), 0
					);
				}, 0);

				// Calculate the difference (new measurement for this aufmass)
				const difference = numericQuantity - previousQuantity;

				if (difference <= 0) {
					showToast(t("aufmass.quantity-must-be-greater-than-previous"), { type: "error" });
					return;
				}

				// Create new group (quick entry - no special flags needed)
				const newGroup = {
					groupName: "Schnelles Aufmaß",
					lines: [{
						text: "Schnelles Eingabe",
						quantity: difference,
						manualQuantity: difference,
						factorOperation: null,
						comments: "",
						sequence: 0
					}]
				};

				const response = await addAufmassGroup({
					projectId,
					aufmassId: formData.id,
					body: { group: newGroup, positionId: invoicePositionId },
				}).unwrap();
				dispatch(applyAufmassResultRaw(response));
				dispatch(markDirtyAfterMutation());

				showToast(t("aufmass.quick-entry-saved"), { type: "success" });
			} catch (error) {
				dispatch(setAufmassError(error?.data?.message || error?.message));
				console.error("Error saving quick entry:", error);
				showToast(error?.data?.message || error?.message || t("aufmass.quick-entry-save-failed"), { type: "error" });
			} finally {
				setSavingQuickEntry(prev => {
					const newState = { ...prev };
					delete newState[position.id];
					return newState;
				});
			}
		}, [reduxPositions, addedGroups, carriedForwardData, dispatch, formData.id, projectId, addAufmassGroup, t]);

		// Handler to add measurement group
		const handleAddGroup = useCallback(
			(position) => {
				dispatch(setAddGroupModal({ show: true, position }));
			},
			[dispatch],
		);

		// Handler for validate button click - show confirmation modal
		const handleValidateClick = (positionId) => {
			setValidatePositionId(positionId);
			setShowValidateModal(true);
		};

		// Handler for confirming validation
		const handleConfirmValidate = async () => {
			if (!validatePositionId) return;

			setIsValidating(true);
			try {
				const response = await validateInvoicePosition({
					projectId,
					invoiceId: invoiceData.id,
					positionId: validatePositionId,
				}).unwrap();
				dispatch(applyAufmassResultRaw(response));
				setShowValidateModal(false);
				setValidatePositionId(null);
			} catch (error) {
				dispatch(setAufmassError(error?.data?.message || error?.message));
				console.error("Error validating position:", error);
			} finally {
				setIsValidating(false);
			}
		};		// Handler for canceling validation
		const handleCancelValidate = () => {
			setShowValidateModal(false);
			setValidatePositionId(null);
		};

		// Handler for saving position comments inline
		const handlePositionCommentSave = useCallback(async (invoicePositionId, comments) => {
			if (!invoiceData.id || !invoicePositionId) return;
			try {
				const response = await updatePositionComments({
					projectId,
					invoiceId: invoiceData.id,
					positionId: invoicePositionId,
					comments,
				}).unwrap();
				dispatch(applyAufmassResultRaw(response));
			} catch (error) {
				showToast(error?.data?.message || error?.message || t("aufmass.comment-save-failed"), { type: "error" });
			}
		}, [dispatch, projectId, invoiceData.id, updatePositionComments, t]);

		const rowProps = (position, index) => ({
			position,
			index,
			positionsData,
			expandedMeasurements,
			toggleMeasurement,
			isReadonly,
			t,
			reduxPositions,
			carriedForwardData,
			addedGroups,
			savingQuickEntry,
			handleQuickEntryBlur,
			handleAddGroup,
			handleValidateClick,
			handlePositionCommentSave,
		});

		return (
			<>
				<div className="positions-table-outer aufmass-positions-table">
					<div className="positions-table-inner">
						<div className="positions-table-header">
							<div className="col-sticky-1" />
							<div className="col-sticky-2">{t("aufmass.invalidation-info")}</div>
							<div className="col-sticky-3">{t("hierarchical-positions.position")}</div>
							<div className="col-sticky-4 positions-table-col-description">
								{t("hierarchical-positions.short-description")}
							</div>
							<div>{t("hierarchical-positions.lv-items")}</div>
							<div>{t("hierarchical-positions.invoice-items")}</div>
							<div>{t("aufmass.previous-measured-items")}</div>
							<div>{t("aufmass.measured-items")}</div>
							<div>{t("hierarchical-positions.unit-lv")}</div>
							<div>{t("hierarchical-positions.unit-price")}</div>
							<div>{t("hierarchical-positions.total-price-lv")}</div>
							<div>{t("hierarchical-positions.inv-amount")}</div>
							<div>{t("hierarchical-positions.inv-calc")}</div>
							<div>{t("aufmass.measured-amount")}</div>
							<div>{t("aufmass.aufmass-calc")}</div>
							<div>{t("hierarchical-positions.warning")}</div>
							<div className="positions-table-col-comments">
								{t("aufmass.position-comments")}
							</div>
						</div>

						{positions.map((position, index) => {
							const hasData =
								position.measurements &&
								position.measurements.length > 0;
							const isExpanded = expandedMeasurements.has(position.id);

							return (
								<React.Fragment key={position.id}>
									<AufmassPositionTableRow
										{...rowProps(position, index)}
									/>
									{isExpanded && (
										<div className="position-expanded-details aufmass-expanded-panel">
											<ExpandedPositionContent
												position={position}
												hasData={hasData}
												onMeasurementChange={onMeasurementChange}
												isReadonly={isReadonly}
											/>
										</div>
									)}
								</React.Fragment>
							);
						})}
					</div>
				</div>

				{showValidateModal && (
					<ModalAtom
						show={showValidateModal}
						title={t("aufmass.confirm-validation")}
						body={t("aufmass.confirm-validation-message")}
						saveText={isValidating ? "common.validating" : "aufmass.validate"}
						handleSubmit={handleConfirmValidate}
						handleClose={handleCancelValidate}
						disabled={isValidating}
					/>
				)}
			</>
		);
	},
);

// Component for expanded position content (measurement tabs and functionality)
const ExpandedPositionContent = React.memo(
	({ position, hasData }) => {
		const { t } = useTranslation();

		// Local state for tab management and comments
		const [activeTab, setActiveTab] = useState("measured");

		const carriedForwardData = useSelector(selectCarriedForwardDataV2);
		const positions =
			useSelector((state) => state.aufmassV2.positions) || {};

		// Get the invoicePositionId for this position to access carried forward data
		const positionData = positions[position.id];
		const invoicePositionId = positionData?.invoicePositionId;

		// Get carried forward groups using the correct invoicePositionId
		const carriedForwardGroups = invoicePositionId
			? carriedForwardData[invoicePositionId] || []
			: [];

		return (
			<>
				<div className="tab-navigation">
					<button
						type="button"
						onClick={() => setActiveTab("measured")}
						className={`tab-button ${activeTab === "measured" ? "active" : ""}`}
					>
						{t("aufmass.measurement-input")}
					</button>

					{hasData && (
						<button
							type="button"
							onClick={() => setActiveTab("measurements")}
							className={`tab-button ${activeTab === "measurements" ? "active" : ""}`}
						>
							{t("aufmass.measurement-data")} (
							{position.measurements?.length || 0})
						</button>
					)}
				</div>

				{/* Tab Content */}
				{activeTab === "measured" && (
					<div className="aufmass-component">
						<div className="position-content mt-3">
							<div className="lines-list">
								{/* Carried forward section */}
								{carriedForwardGroups?.length ? (
									<CarriedForwardSectionV2
										position={position}
									/>
								) : null}

								{/* New measurement section */}
								<NewMeasurementSectionV2 position={position} />
							</div>
						</div>
					</div>
				)}

				{activeTab === "measurements" && hasData && (
					<div className="measurements-tab">
						<h6>
							{t("aufmass.measurement-data-for-position")}{" "}
							{position.itemNumber ||
								position.id ||
								t("aufmass.unknown")}
						</h6>
						<div className="measurements-list">
							{position.measurements?.map(
								(measurement, index) => (
									<div key={index} className="measurement-item">
										<div className="title">
											{t("aufmass.measurement")} {index + 1}
										</div>
										<div className="content">
											{JSON.stringify(measurement, null, 2)}
										</div>
									</div>
								),
							)}
						</div>
					</div>
				)}
			</>
		);
	},
);

export default ExpandablePositionsTable;
